import React, { useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';

import { fetchGroups } from '../../axiosClient/authApi';
import { postContractType } from '../../axiosClient/dictionaryApi';
import ControlledTextField from '../../components/ControlledTextField';
import CustomChip from '../../components/CustomChip';
import CustomModal from '../../components/CustomModal';
import LinkSelectedFiled from '../../components/LinkSelectedTagsField';
import RISelectComponent from '../../components/SelectComponent';
import { IOptions } from '../../interfaces/IOptions';

interface IProps {
  open: boolean;
  handleClose: VoidFunction;
}

const analysisOptions: IOptions[] = [
  { id: '1', name: 'DTD Covenant Report' },
  { id: '2', name: 'Regular Insight Report' },
];

const CreateContractType: React.FC<IProps> = ({ open, handleClose }) => {
  const methods = useForm({
    defaultValues: {
      groups: [1],
      tags: [],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = methods;

  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const { data: groupListData, isLoading: groupLoading } = useQuery({
    queryKey: ['GroupList'],
    queryFn: async () => {
      const response = await fetchGroups();
      return response.results;
    },
  });

  const { mutate, status } = useMutation({
    mutationKey: ['add_contract_type'],
    mutationFn: postContractType,
    onSuccess: () => {
      enqueueSnackbar('Contract added successfully!', {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      queryClient.invalidateQueries({
        queryKey: ['contracts'],
      });
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to add Contract!', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = useCallback((data: any) => {
    mutate(data);
  }, []);

  return (
    <CustomModal
      title="Add new contract type"
      open={open}
      maxWidth="md"
      handleClose={handleClose}
    >
      <FormProvider {...methods}>
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          mt={2}
          spacing={2}
          sx={{ width: '500px', padding: '30px 10px' }}
        >
          <ControlledTextField
            name="contract_type"
            variant="outlined"
            label="Document Type"
            required
            fullWidth
          />
          <RISelectComponent
            name="analysis_type"
            label="Analysis Type"
            control={control}
            required
            options={analysisOptions}
            valueKey="name"
          />
          <LinkSelectedFiled name="tags" />
          <RISelectComponent
            name="groups"
            control={control}
            label="Prescribing Department"
            options={groupListData || []}
            loading={groupLoading}
            isMultiselect={true}
            required
            renderCustomComponent={(value: IOptions | string | number) => (
              <CustomChip
                key={(value as IOptions)?.id}
                label={(value as IOptions)?.name}
              />
            )}
          />
          <LoadingButton
            type="submit"
            variant="contained"
            loading={status === 'pending'}
            fullWidth
            disabled={!isValid}
          >
            Add
          </LoadingButton>
        </Stack>
      </FormProvider>
    </CustomModal>
  );
};

export default CreateContractType;
